import { useState, useRef, useEffect } from "react";
import Dropzone from "react-dropzone";
import removeSvg from "../../../assets/images/remove.svg";
import checkSvg from "./check.svg";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useToast } from "../../../hooks/ToastContext";

// ----------------------------------------------------------------------

const acceptedFiles = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "application/pdf",
];

const maxFileSize = 5 * 1024 * 1024;

// ----------------------------------------------------------------------

export function InputFileMultiple({
  onChange,
  uploadedIndex,
}: {
  onChange: (files: File[]) => void;
  uploadedIndex: number;
}) {
  const { showToast } = useToast();

  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (newFiles: File[]) => {
    for (let i = 0; i < newFiles.length; i++) {
      if (newFiles[i].size > maxFileSize) {
        showToast("error", "Tamanho máximo de 5mb por arquivo");
        return;
      }

      if (!acceptedFiles.includes(newFiles[i].type)) {
        showToast(
          "error",
          "Formato inválido, apenas PDF, PNG, JPG e JPEG são aceitos"
        );
        return;
      }
    }

    setFiles((files) => [...files, ...newFiles]);
  };

  const removeFile = (file: File) => {
    setFiles((files) => files.filter((item) => item !== file));
  };

  useEffect(() => {
    onChange(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  // ----------------------------------------------------------------------

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        overflow: "hidden",
        flexDirection: "column",
      }}
    >
      <FilesList
        files={files}
        uploadedIndex={uploadedIndex}
        onRemove={removeFile}
      />

      <Dropzone onDrop={addFiles}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                addFiles(files);
              }}
              accept={acceptedFiles.join(", ")}
              {...getInputProps()}
            />

            <Stack
              {...getRootProps()}
              sx={{
                cursor: "pointer",
                width: "100%",
                padding: "0.75rem 1rem",
                borderRadius: 2,
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgb(224, 224, 224)",
                "& p": {
                  m: 0,
                },
                ...(isDragActive && {
                  border: "1px dashed rgb(224, 224, 224)",
                }),
              }}
            >
              <p
                style={{
                  fontSize: 12,
                  color: "#757575",
                  textAlign: "center",
                  ...(isDragActive && {
                    opacity: 0.5,
                  }),
                }}
              >
                <p style={{ marginBottom: "0.5rem" }}>
                  Arraste seu arquivo aqui ou{" "}
                  <span style={{ textDecoration: "underline", fontSize: 13 }}>
                    selecione
                  </span>
                  , máx 5Mb
                </p>
                <p>São permitidos apenas PDF, PNG, JPG, JPEG</p>
              </p>
            </Stack>
          </>
        )}
      </Dropzone>
    </Box>
  );
}

// ----------------------------------------------------------------------

function FilesList({
  files,
  uploadedIndex,
  onRemove,
}: {
  files: File[];
  onRemove: (file: File) => void;
  uploadedIndex: number;
}) {
  if (!files.length) return null;

  return (
    <Stack
      sx={{
        width: "100%",
        py: 1,
        px: 1,
        borderRadius: 2,
        border: "1px solid rgb(224, 224, 224)",
        marginBottom: 1,
      }}
    >
      {files.map((file, index) => (
        <Box key={index}>
          {index !== 0 && (
            <Box
              sx={{
                mx: 1,
                borderBottom: "1px solid #e6e6e6",
              }}
            />
          )}

          <Stack
            direction="row"
            sx={{
              px: 1,
              gap: 2,
              alignItems: "center",
              borderRadius: 2,
              ...(index < uploadedIndex && {
                bgcolor: "#13A66633",
              }),
            }}
          >
            <Typography
              sx={{
                flex: 1,
                py: 1,
                fontSize: 13,
                color: "#525252",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                ...(index < uploadedIndex && {
                  color: "#13A666",
                }),
              }}
            >
              {file.name}
            </Typography>

            {index < uploadedIndex ? (
              <Stack
                sx={{
                  backgroundColor: "#13A666",
                  borderRadius: 999,
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                }}
              >
                <img src={checkSvg} width={26} height={26} alt="" />
              </Stack>
            ) : (
              <Tooltip title="Excluir anexo">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(file);
                  }}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#e6e6e6",
                    "&:hover": {
                      bgcolor: "#d4d4d4",
                    },
                  }}
                >
                  <img src={removeSvg} width={12} height={12} alt="" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
